import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock3, ShieldCheck } from 'lucide-react';

// Target-v2 committed work (dark). Renders only for workflow_version 2.
//
// Committed work is binding: there is no Accept, no Decline, and no countdown to
// respond to. pickup_by is an execution deadline, not an offer timer — passing it
// escalates to dispatch and never silently removes the Assignment.

const REASONS = [
  { code: 'VEHICLE_ISSUE', label: 'Vehicle problem' },
  { code: 'MEDICAL_EMERGENCY', label: 'Medical emergency' },
  { code: 'SAFETY_EMERGENCY', label: 'Safety emergency' },
  { code: 'DEVICE_CONNECTIVITY', label: 'Phone or signal problem' },
  { code: 'TEMPORARY_DELAY', label: 'Temporary delay' },
  { code: 'OTHER', label: 'Something else' },
];

const buttonClass = 'min-h-11 rounded-xl px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-45';
const fieldClass = 'mt-1 min-h-11 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-stone-700 dark:bg-stone-900 dark:text-white';

function remainingLabel(pickupBy, now) {
  if (!pickupBy) return null;
  const deltaMs = new Date(pickupBy).getTime() - now;
  if (Number.isNaN(deltaMs)) return null;
  const overdue = deltaMs < 0;
  const totalMinutes = Math.floor(Math.abs(deltaMs) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const span = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  return { overdue, text: overdue ? `${span} past the pickup deadline` : `${span} until the pickup deadline` };
}

export default function RiderCommittedWorkCard({ assignment, busy, problem, onReportProblem }) {
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('VEHICLE_ISSUE');
  const [note, setNote] = useState('');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!assignment?.pickupBy) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [assignment?.pickupBy]);

  if (Number(assignment?.workflowVersion) !== 2) return null;

  const deadline = remainingLabel(assignment.pickupBy, now);
  const hasOpenProblem = problem?.status === 'OPEN' || problem?.status === 'REASSIGNMENT_QUEUED';
  const noteRequired = reasonCode === 'OTHER';
  const canSubmit = !noteRequired || note.trim().length >= 10;

  return (
    <section
      className="mt-4 rounded-2xl border border-stone-200 p-4 dark:border-stone-800"
      aria-labelledby={`committed-${assignment.id}`}
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" aria-hidden="true" />
        <div className="min-w-0">
          <h3 id={`committed-${assignment.id}`} className="font-semibold">This delivery is assigned to you</h3>
          <p className="mt-1 text-sm leading-5 text-stone-500 dark:text-stone-400">
            It is already assigned to you. If you cannot do it, report the problem
            and your store will decide.
          </p>
        </div>
      </div>

      {deadline && (
        <p
          className={`mt-3 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
            deadline.overdue
              ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-100'
              : 'bg-stone-50 text-stone-700 dark:bg-stone-900 dark:text-stone-200'
          }`}
        >
          <Clock3 className="h-4 w-4 shrink-0" aria-hidden="true" />
          {deadline.text}
        </p>
      )}

      {hasOpenProblem ? (
        <div
          role="status"
          className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
        >
          <p className="font-semibold">Problem reported</p>
          <p className="mt-1 leading-5">
            {problem.status === 'REASSIGNMENT_QUEUED'
              ? 'Your store confirmed this and is reassigning the delivery. Keep any parcels safe until told otherwise.'
              : 'Your store has been notified and will respond. This delivery stays assigned to you until they decide.'}
          </p>
        </div>
      ) : !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`${buttonClass} mt-3 inline-flex items-center gap-2 border border-stone-300 dark:border-stone-700`}
        >
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          Report a problem
        </button>
      ) : (
        <div className="mt-3 grid gap-3 rounded-xl border border-stone-200 p-3 dark:border-stone-800">
          <p className="text-sm leading-5 text-stone-500 dark:text-stone-400">
            Tell your store what happened. Reporting does not cancel the delivery.
          </p>
          <label className="text-xs font-medium text-stone-600 dark:text-stone-300">
            What is the problem?
            <select
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
              className={fieldClass}
            >
              {REASONS.map((reason) => (
                <option key={reason.code} value={reason.code}>{reason.label}</option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-stone-600 dark:text-stone-300">
            Details {noteRequired ? '(required)' : '(optional)'}
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value.slice(0, 500))}
              rows="3"
              maxLength="500"
              className={`${fieldClass} py-2`}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setNote(''); }}
              className={`${buttonClass} border border-stone-300 dark:border-stone-700`}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !canSubmit}
              onClick={() => onReportProblem({
                assignmentId: assignment.id,
                expectedAssignmentVersion: assignment.version,
                reasonCode,
                note: note.trim() || undefined,
              })}
              className={`${buttonClass} bg-amber-600 text-white`}
            >
              {busy ? 'Sending…' : 'Send report'}
            </button>
          </div>
        </div>
      )}

      {problem?.status === 'RESOLVED' && (
        <p
          role="status"
          className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-950 dark:bg-emerald-950/30 dark:text-emerald-100"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Your store reviewed the problem and this delivery is still yours.
        </p>
      )}
    </section>
  );
}
